import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, parseNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Truck, Plus, Pencil, Trash2, History } from "lucide-react";
import { DataTable } from "@/components/DataTable";
import { toast } from "sonner";

const empty = { name: "", plate: "", type: "", cost_per_hour: "", consumption_type: "km", avg_consumption: "" };
const fmtEur = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" }) : "-";
const STATUS_LABEL = { in_attesa: "In attesa", approvata: "Approvata", rifiutata: "Rifiutata" };

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [types, setTypes] = useState([]);

  const load = () => api.get("/vehicles").then((r) => setItems(r.data));
  const loadTypes = () => api.get("/vehicle-types").then((r) => setTypes(r.data));
  useEffect(() => { load(); loadTypes(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, type: types[0]?.name || "" }); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ name: v.name, plate: v.plate, type: v.type, cost_per_hour: v.cost_per_hour ?? "", consumption_type: v.consumption_type || "km", avg_consumption: v.avg_consumption ?? "" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, cost_per_hour: parseNum(form.cost_per_hour) || 0, avg_consumption: parseNum(form.avg_consumption) || 0 };
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(editing ? "Mezzo aggiornato" : "Mezzo aggiunto");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Mezzo eliminato");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openHistory = async (v) => {
    setHistory({ vehicle: v, data: null });
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/vehicles/${v.id}/requests`);
      setHistory({ vehicle: v, data });
    } catch (err) {
      toast.error("Errore nel caricamento dello storico");
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Mezzi</h1>
          <p className="mt-1 text-sm text-slate-500">Gestisci i mezzi disponibili per il rifornimento.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="add-vehicle-btn">
              <Plus className="mr-2 h-4 w-4" /> Aggiungi mezzo
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="vehicle-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Modifica mezzo" : "Nuovo mezzo"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="v-name">Nome / Modello</Label>
                <Input id="v-name" data-testid="vehicle-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="es. Fiat Ducato" />
              </div>
              <div>
                <Label htmlFor="v-plate">Targa</Label>
                <Input id="v-plate" data-testid="vehicle-plate-input" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} className="mt-1.5" placeholder="es. AB123CD" />
              </div>
              <div>
                <Label>Tipologia</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t.id} value={t.name} className="capitalize">{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="v-cost">Costo orario (€) — per rapportini</Label>
                <Input id="v-cost" type="text" inputMode="decimal" step="0.01" data-testid="vehicle-cost-input" value={form.cost_per_hour} onChange={(e) => setForm({ ...form, cost_per_hour: e.target.value })} className="mt-1.5" placeholder="0,00" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo consumo</Label>
                  <Select value={form.consumption_type} onValueChange={(v) => setForm({ ...form, consumption_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="vehicle-consumption-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="km">km / litro</SelectItem>
                      <SelectItem value="ore">ore / litro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="v-cons">Consumo medio previsto</Label>
                  <Input id="v-cons" type="text" inputMode="decimal" step="0.01" data-testid="vehicle-avg-consumption-input" value={form.avg_consumption} onChange={(e) => setForm({ ...form, avg_consumption: e.target.value })} className="mt-1.5" placeholder={form.consumption_type === "ore" ? "es. 2,5 ore/l" : "es. 12,5 km/l"} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={save} disabled={!form.name} data-testid="save-vehicle-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="p-4">
          <DataTable
            data={items}
            testid="vehicles-table"
            rowTestId={(v) => `vehicle-row-${v.id}`}
            searchKeys={["name", "plate", "type"]}
            searchPlaceholder="Cerca mezzo..."
            initialSort={{ key: "name", dir: "asc" }}
            emptyIcon={Truck}
            emptyText="Nessun mezzo"
            columns={[
              { key: "name", header: "Nome", sortable: true, className: "font-medium text-slate-900", cell: (v) => v.name },
              { key: "plate", header: "Targa", sortable: true, className: "text-slate-600", cell: (v) => v.plate || "-" },
              { key: "type", header: "Tipologia", sortable: true, className: "capitalize text-slate-600", cell: (v) => v.type },
              { key: "avg_consumption", header: "Consumo previsto", align: "right", sortable: true, className: "text-right text-slate-600", cell: (v) => v.avg_consumption ? `${v.avg_consumption.toLocaleString("it-IT")} ${v.consumption_type === "ore" ? "ore/l" : "km/l"}` : <span className="text-slate-300">—</span> },
              { key: "cost_per_hour", header: "Costo orario", align: "right", sortable: true, className: "text-right text-slate-600", cell: (v) => v.cost_per_hour ? fmtEur(v.cost_per_hour) : <span className="text-slate-300">—</span> },
              { key: "actions", header: "Azioni", align: "right", cell: (v) => (
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openHistory(v)} data-testid={`history-vehicle-${v.id}`} className="rounded-md">
                      <History className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`} className="rounded-md">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDeleteTarget(v)} data-testid={`delete-vehicle-${v.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) },
            ]}
          />
        </div>
      </Card>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-w-2xl" data-testid="vehicle-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Storico mezzo {history?.vehicle?.name}
              {history?.vehicle?.plate ? ` (${history.vehicle.plate})` : ""}
            </DialogTitle>
            <DialogDescription>Totale speso e richieste collegate a questo mezzo.</DialogDescription>
          </DialogHeader>
          {historyLoading || !history?.data ? (
            <p className="py-8 text-center text-sm text-slate-400">Caricamento...</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Speso (approvato)</p>
                  <p className="font-heading text-xl font-extrabold text-[#002FA7]" data-testid="vehicle-history-total-approved">
                    {fmtEur(history.data.total_approved)}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">In attesa</p>
                  <p className="font-heading text-xl font-extrabold text-amber-600">{fmtEur(history.data.total_pending)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Richieste</p>
                  <p className="font-heading text-xl font-extrabold text-slate-900">{history.data.count}</p>
                </div>
              </div>

              {history.data.requests.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Nessuna richiesta collegata a questo mezzo.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                  <Table data-testid="vehicle-history-table">
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Dipendente</TableHead>
                        <TableHead>Utilizzo</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead>Stato</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.data.requests.map((r) => (
                        <TableRow key={r.id} className="hover:bg-slate-50">
                          <TableCell className="text-slate-500">{fmtDate(r.created_at)}</TableCell>
                          <TableCell className="text-slate-700">{r.employee_name}</TableCell>
                          <TableCell className="text-slate-600">{r.usage_value} {r.usage_type === "km" ? "km" : "ore"}</TableCell>
                          <TableCell className="text-right font-heading font-bold text-slate-900">{fmtEur(r.amount)}</TableCell>
                          <TableCell className="text-slate-600">{STATUS_LABEL[r.status] || r.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-vehicle-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminare {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Il mezzo {deleteTarget?.plate ? `(${deleteTarget.plate}) ` : ""}verrà rimosso definitivamente. Le richieste già inviate restano nello storico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { await remove(deleteTarget.id); setDeleteTarget(null); }}
              data-testid="confirm-delete-vehicle-btn"
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
